import React from 'react';
import { IPresenterProps } from './types';
import * as S from './styles';
import { Calendar, Clock, Scissors, Check } from 'lucide-react';

const LOGO_URL = 'https://prod-image-uploads-mora.s3.sa-east-1.amazonaws.com/logo192.png';

const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const CancelBookingPresenter = (props: IPresenterProps) => {
    const {
        isLoading,
        booking,
        error,
        isCancelling,
        isCancelled,
        onConfirmCancel,
        onGoHome
    } = props;

    const renderLoading = () => (
        <S.Card>
            <S.LoadingContainer>
                <S.Spinner />
                Cargando detalles de la reserva...
            </S.LoadingContainer>
        </S.Card>
    );

    const renderError = () => (
        <S.Card>
            <S.ErrorContainer>
                <S.Title>Error</S.Title>
                <S.ErrorText>{error}</S.ErrorText>
                <S.Button $variant="secondary" onClick={onGoHome}>
                    Volver al inicio
                </S.Button>
            </S.ErrorContainer>
        </S.Card>
    );

    const renderSuccess = () => (
        <S.Card>
            <S.SuccessContainer>
                <S.SuccessIcon>
                    <Check size={40} />
                </S.SuccessIcon>
                <S.SuccessTitle>Reserva Cancelada</S.SuccessTitle>
                <S.SuccessText>
                    Tu reserva ha sido cancelada exitosamente.
                </S.SuccessText>
                <S.Button $variant="secondary" onClick={onGoHome}>
                    Volver al inicio
                </S.Button>
            </S.SuccessContainer>
        </S.Card>
    );

    const renderCancelForm = () => {
        if (!booking) return null;

        const servicesText = booking.services
            ?.map(s => s.name)
            .join(', ') || 'Servicio';

        return (
            <S.Card>
                <S.Title>Cancelar esta reserva?</S.Title>

                <S.BookingCard>
                    <S.BusinessName>{booking.businessName}</S.BusinessName>

                    <S.DetailRow>
                        <Calendar size={16} />
                        {booking.startTimestamp
                            ? formatDate(booking.startTimestamp)
                            : 'Fecha no disponible'}
                    </S.DetailRow>

                    <S.DetailRow>
                        <Clock size={16} />
                        {booking.selectedTime?.startTime || 'Hora no disponible'}
                    </S.DetailRow>

                    <S.DetailRow>
                        <Scissors size={16} />
                        {servicesText}
                    </S.DetailRow>

                    {booking.services && booking.services.length > 0 && (
                        <S.ServicesList>
                            {booking.services.map((s, idx) => (
                                <S.ServiceItem key={idx}>
                                    {s.name} {s.price ? `- $${s.price}` : ''}
                                </S.ServiceItem>
                            ))}
                        </S.ServicesList>
                    )}

                    {booking.totalCost > 0 && (
                        <S.TotalCost>
                            Total: ${booking.totalCost}
                        </S.TotalCost>
                    )}
                </S.BookingCard>

                <S.ButtonsContainer>
                    <S.Button
                        $variant="danger"
                        onClick={onConfirmCancel}
                        disabled={isCancelling}
                    >
                        {isCancelling ? 'Cancelando...' : 'Confirmar Cancelacion'}
                    </S.Button>
                    <S.Button $variant="secondary" onClick={onGoHome}>
                        Volver
                    </S.Button>
                </S.ButtonsContainer>
            </S.Card>
        );
    };

    return (
        <S.PageContainer>
            <S.Container>
                <S.LogoContainer>
                    <S.Logo src={LOGO_URL} alt="Mora" />
                </S.LogoContainer>

                {isLoading && renderLoading()}
                {error && !isLoading && renderError()}
                {isCancelled && renderSuccess()}
                {!isLoading && !error && !isCancelled && booking && renderCancelForm()}

                <S.Footer>
                    <S.FooterLink href="https://mora-booking.com">
                        mora-booking.com
                    </S.FooterLink>
                    <S.FooterText>
                        Sistema de reservas online
                    </S.FooterText>
                </S.Footer>
            </S.Container>
        </S.PageContainer>
    );
};

export default CancelBookingPresenter;
